import { articleContent } from '@/mocks/articleContent';
import { guideArticles } from '@/mocks/articles';

export const SITE_URL: string =
  (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://allchinabuyspread.boats';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  path: string;
  type: 'website' | 'article';
}

const HOME_TITLE = 'AllChinaBuy Spreadsheet – Products, Sellers, QC & Shipping Guide';
const HOME_DESCRIPTION =
  'Explore the AllChinaBuy Spreadsheet with product lists, trusted sellers, QC tips, shipping information, warehouse guides, reviews and practical buying resources.';
const HOME_KEYWORDS =
  'AllChinaBuy, AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy warehouse, AllChinaBuy QC, AllChinaBuy legit, AllChinaBuy guide';

function homeSeo(path: string): SeoData {
  return {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    keywords: HOME_KEYWORDS,
    path,
    type: 'website',
  };
}

function articleSeo(slug: string): SeoData {
  const content = articleContent[slug];
  const guide = guideArticles.find((a) => a.slug === slug);
  const title = content?.title ?? guide?.title ?? 'AllChinaBuy Guide';
  const description =
    guide?.desc ??
    content?.sections?.[0]?.content?.slice(0, 160) ??
    'Practical AllChinaBuy guide with tips, resources and step-by-step buying advice.';
  return {
    title: `${title} | AllChinaBuy Spreadsheet`,
    description,
    keywords: `AllChinaBuy, ${title}, AllChinaBuy Spreadsheet, AllChinaBuy guide, AllChinaBuy shipping`,
    path: `/${slug}/`,
    type: 'article',
  };
}

const staticPages: Record<string, SeoData> = {
  '/about/': {
    title: 'About Us | AllChinaBuy Spreadsheet',
    description:
      'Learn about AllChinaBuy Spreadsheet, an independent resource for product discovery, seller research, QC tips and shipping guidance.',
    keywords: 'About AllChinaBuy Spreadsheet, AllChinaBuy guide, buying agent information',
    path: '/about/',
    type: 'website',
  },
  '/contact/': {
    title: 'Contact Us | AllChinaBuy Spreadsheet',
    description:
      'Contact AllChinaBuy Spreadsheet for questions and feedback about our buying guides, QC tips and shipping resources.',
    keywords: 'Contact AllChinaBuy Spreadsheet, AllChinaBuy help, buying guide feedback',
    path: '/contact/',
    type: 'website',
  },
  '/safety/': {
    title: 'Safety Center | AllChinaBuy Spreadsheet',
    description:
      'AllChinaBuy safety center with essential guidance on seller verification, account security, payment safety, QC checks and common scams.',
    keywords: 'AllChinaBuy safety, AllChinaBuy legit, seller verification, AllChinaBuy scams',
    path: '/safety/',
    type: 'website',
  },
  '/privacy-policy/': {
    title: 'Privacy Policy | AllChinaBuy Spreadsheet',
    description:
      'Privacy policy for AllChinaBuy Spreadsheet describing how we handle information when you visit our website.',
    keywords: 'AllChinaBuy Spreadsheet privacy policy, website privacy',
    path: '/privacy-policy/',
    type: 'website',
  },
  '/terms/': {
    title: 'Terms & Conditions | AllChinaBuy Spreadsheet',
    description:
      'Terms and conditions for using AllChinaBuy Spreadsheet, an independent informational resource for buying guidance.',
    keywords: 'AllChinaBuy Spreadsheet terms, terms and conditions',
    path: '/terms/',
    type: 'website',
  },
  '/disclaimer/': {
    title: 'Disclaimer | AllChinaBuy Spreadsheet',
    description:
      'Disclaimer for AllChinaBuy Spreadsheet regarding the informational nature of our content and buying risks.',
    keywords: 'AllChinaBuy Spreadsheet disclaimer, buying risk disclosure',
    path: '/disclaimer/',
    type: 'website',
  },
};

export function getSeoData(pathname: string): SeoData {
  const path = pathname || '/';

  if (path === '/' || path === '/en/' || path === '/fr/' || path === '/da/' || path === '/de/') {
    return homeSeo(path);
  }

  const slug = path.replace(/^\//, '').replace(/\/$/, '');
  if (articleContent[slug]) {
    return articleSeo(slug);
  }

  return staticPages[path] ?? homeSeo(path);
}